import { defineStore } from "pinia";
import { invalidateApiCache, useApiCall } from "~/composables/useApiCall";
import { extractHostname } from "~/utils/ExtractHostname";
import { useSearchStore } from "~/store/search";
import { useOrgStore } from "~/store/org";
import { useManageStore } from "~/store/manage";
import MediaListDomainAttributes from "~/types/attributes/MediaListDomainAttributesType";
import MediaListState, {
  MediaListDropdown,
  MediaListItem,
  MediaListDomains,
  MediaListDomainItem,
  MediaLists,
} from "~/types/MediaListStateType";
import MediaListDropdownItem from "~/types/MediaListDropdownItemType";
import RequestOptions from "~/types/RequestOptionsType";

export const useMediaListStore = defineStore("mediaList", {
  state: (): MediaListState => ({
    mediaLists: [],
    mediaListDomains: [],
    mediaListMap: new Map(),
    mediaListDomainMap: new Map(),
    mediaListIdDomainMap: new Map(),
    includedMediaLists: [],
    includedMediaListsBackup: [],
    excludedMediaLists: [],
    excludedMediaListsBackup: [],
    mediaListSuggestions: [],
  }),

  getters: {
    remainingMediaListUsage(_state): any {
      const manageSearchStore = useManageStore();
      const orgStore = useOrgStore();

      return orgStore.getTMLDomainsLimit
        ? orgStore.getTMLDomainsLimit - manageSearchStore.total
        : 0;
    },
    getIncludedMediaLists(state) {
      return state.includedMediaLists;
    },
    getExcludedMediaLists(state) {
      return state.excludedMediaLists;
    },
    mediaListDropdownItems(state) {
      const dropdownItems: MediaListDropdown = [];
      state.mediaLists.forEach((mediaList: MediaListItem) => {
        if (
          !state.includedMediaLists.find((list) => list.id === mediaList.id) &&
          !state.excludedMediaLists.find((list) => list.id === mediaList.id)
        ) {
          const count =
            mediaList?.relationships?.media_list_domains?.data?.length || 0;
          dropdownItems.push({
            name: `${mediaList?.attributes?.name} (${count})`,
            id: mediaList.id,
          });
        }
      });
      return dropdownItems;
    },
    mediaListOptions(state): { label: string; value: number }[] {
      return state.mediaLists.map((mediaList: MediaListItem) => ({
        label: `${mediaList?.attributes?.name}
          (${mediaList?.relationships?.media_list_domains?.data?.length})`,
        value: Number(mediaList.id),
      }));
    },
    getMediaListDomainsForTML(state) {
      return (id: string) => {
        return state.mediaListDomains.filter(
          (mediaListDomain) =>
            mediaListDomain.relationships.media_list.data.id === id,
        );
      };
    },
  },

  actions: {
    clearMediaListSuggestions() {
      this.mediaListSuggestions = [];
    },
    setIncludedMediaLists(list: MediaListDropdown) {
      this.includedMediaLists = list;
      this.includedMediaListsBackup = list;
    },
    setExcludedMediaLists(list: MediaListDropdown) {
      this.excludedMediaLists = list;
      this.excludedMediaListsBackup = list;
    },
    mapMediaListDomains(mediaListDomains: MediaListDomains) {
      mediaListDomains.forEach((domainObj: MediaListDomainItem) => {
        // map media list domain names to their respective media list id
        const mediaListId = domainObj?.relationships?.media_list?.data?.id;
        this.mediaListDomainMap.set(
          domainObj?.attributes?.media_type === "web"
            ? extractHostname(domainObj?.attributes?.url)
            : domainObj?.attributes?.url,
          mediaListId,
        );

        // map media list domain id to their respective media list domain obj
        if (this.mediaListIdDomainMap.has(mediaListId)) {
          const arr = this.mediaListIdDomainMap.get(mediaListId);
          if (arr) {
            arr.push(domainObj?.attributes);
            this.mediaListIdDomainMap.set(mediaListId, arr);
          }
        } else {
          this.mediaListIdDomainMap.set(mediaListId, [domainObj?.attributes]);
        }
      });
    },
    mapMediaLists(mediaListArray: MediaLists) {
      // map the ids of each media list to their respective list names
      mediaListArray.forEach((listObj: MediaListItem) => {
        this.mediaListMap.set(listObj.id, listObj?.attributes?.name);
      });
    },
    addIncludedMediaList(dropdownItem: MediaListDropdownItem) {
      // Add tag to included media lists + remove from dropdown
      const savedSearchStore = useSearchStore();
      this.includedMediaLists = [...this.includedMediaLists, dropdownItem];
      savedSearchStore.articleSearchRequest();
    },

    removeIncludedMediaList(id: string) {
      // Remove tag from included media lists + add it back to dropdown
      const savedSearchStore = useSearchStore();
      this.includedMediaLists = this.includedMediaLists.filter(
        (dropdownItem: MediaListDropdownItem) => {
          return dropdownItem.id !== id;
        },
      );
      savedSearchStore.articleSearchRequest();
    },

    addExcludedMediaList(dropdownItem: MediaListDropdownItem) {
      // Add tag to excluded media lists + remove from dropdown
      const savedSearchStore = useSearchStore();
      this.excludedMediaLists = [...this.excludedMediaLists, dropdownItem];
      savedSearchStore.articleSearchRequest();
    },

    removeExcludedMediaList(id: string) {
      // Remove tag from excluded media lists + add it back to dropdown
      const savedSearchStore = useSearchStore();
      this.excludedMediaLists = this.excludedMediaLists.filter(
        (dropdownItem: MediaListDropdownItem) => {
          return dropdownItem.id !== id;
        },
      );
      savedSearchStore.articleSearchRequest();
    },

    async fetchMediaListSuggestions(searchTerm: string, mediaType: string) {
      const encodedSearchTerm = encodeURIComponent(searchTerm);
      const encodedMediaType = encodeURIComponent(mediaType);

      const options: RequestOptions = {
        method: "GET",
      };

      const resp = await useApiCall(
        `/publications/suggestions?search_term=${encodedSearchTerm}&media_type=${encodedMediaType}`,
        options,
      );
      const responseData = resp.data._rawValue;
      if (responseData.errors) {
        this.mediaListSuggestions = [];
      } else {
        this.mediaListSuggestions = responseData;
      }
    },

    async fetchMediaLists() {
      const options: RequestOptions = {
        method: "GET",
      };
      const resp = await useApiCall("/media_lists", options);
      const responseData: MediaLists = resp.data._rawValue.data;
      this.mediaLists = responseData;
      this.mediaListDomains = resp.data._rawValue.included || [];
      this.mediaListIdDomainMap = new Map();

      this.mapMediaLists(this.mediaLists);
      this.mapMediaListDomains(this.mediaListDomains);
    },
    async createMediaList(listName: string) {
      const reqQuery = {
        data: {
          attributes: {
            name: listName,
            created_at: null,
          },
          type: "media-lists",
        },
      };
      const options: RequestOptions = { method: "POST", body: reqQuery };

      const resp = await useApiCall(`/media_lists`, options);

      if (resp?.error?.value?.data?.errors) {
        throw resp.error.value.data.errors[0].detail;
      }

      // Invalidate cache for media lists to ensure fresh data
      invalidateApiCache("/media_lists");

      return resp;
    },
    async updateMediaListName(id: string, newListName: string) {
      const reqQuery = {
        data: {
          attributes: {
            name: newListName,
          },
          type: "media-lists",
        },
      };
      const options: RequestOptions = { method: "PATCH", body: reqQuery };

      const resp = await useApiCall(`/media_lists/${id}`, options);

      if (resp?.error?.value?.data?.errors) {
        throw resp.error.value.data.errors[0].detail;
      }

      // Update the local state to reflect the name change
      invalidateApiCache("/media_lists");

      const foundMediaList = this.mediaLists.find(
        (mediaList) => mediaList.id === id,
      );

      if (foundMediaList) {
        // Update the name property in the attributes field
        foundMediaList.attributes.name = newListName;
      }
    },
    async deleteTargetMediaList(id: string) {
      const options: RequestOptions = {
        method: "DELETE",
      };
      try {
        const resp = await useApiCall(`/media_lists/${id}`, options);

        // filter out the deleted mediaLists
        this.mediaLists = this.mediaLists.filter(
          (mediaList) => mediaList.id !== id,
        );
        // Invalidate cache for media lists to ensure fresh data
        invalidateApiCache("/media_lists");
        return resp;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },
    async updateDomainName(id: string, newDomainName: string) {
      try {
        const reqQuery = {
          data: {
            attributes: {
              name: newDomainName,
            },
            type: "media-list-domains",
          },
        };
        const options: RequestOptions = { method: "PATCH", body: reqQuery };
        const resp = await useApiCall(`/media_list_domains/${id}`, options);
        await this.fetchMediaLists();

        return resp;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },
    async addTargetMediaListDomain(domain: MediaListDomainAttributes) {
      const reqQuery = {
        data: {
          attributes: {
            name: "",
            media_type: domain.mediaType,
            url: domain.url,
          },
          relationships: {
            media_list: {
              data: {
                type: "media-lists",
                id: domain.mediaList,
              },
            },
          },
          type: "media-list-domains",
        },
      };

      if (domain?.name) {
        reqQuery.data.attributes.name = domain?.name;
      }

      const options: RequestOptions = {
        method: "POST",
        body: reqQuery,
      };
      const resp = await useApiCall(`/media_list_domains`, options);

      if (resp?.error?.value?.data?.errors) {
        throw resp.error.value.data.errors[0].detail;
      }

      // Invalidate cache for media lists to ensure fresh data

      invalidateApiCache("/media_list_domains");
      invalidateApiCache("/media_lists");

      this.mediaListDomains.push(resp.data._rawValue.data);
    },
    async deleteTargetMediaListDomain(id: string) {
      const options: RequestOptions = {
        method: "DELETE",
      };
      try {
        const resp = await useApiCall(`/media_list_domains/${id}`, options);

        // filter out the deleted mediaLists
        this.mediaListDomains = this.mediaListDomains.filter(
          (mediaList) => mediaList.id !== id,
        );

        // Invalidate cache for media lists to ensure fresh data
        invalidateApiCache("/media_list_domains");
        invalidateApiCache("/media_lists");

        return resp;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      }
    },
    resetMediaLists() {
      this.includedMediaLists = this.includedMediaListsBackup;
      this.excludedMediaLists = this.excludedMediaListsBackup;
    },
    getPubsfromIdsForMediaLists(ids: Set<string>) {
      const searchStore = useSearchStore();
      const idArrays = Array.from(ids);
      const filteredMentions = searchStore.mentions.filter((mention) =>
        idArrays.includes(mention.id),
      );
      const seen = new Set<string>();
      const uniqueArray: { media_type: string; url: string; name: string }[] =
        [];

      for (const mention of filteredMentions) {
        const key = `${mention.media_type}-${mention.domain}-${mention.publication}`;

        if (!seen.has(key)) {
          seen.add(key);
          uniqueArray.push({
            media_type: mention.media_type,
            url: mention.domain,
            name: mention.publication,
          });
        }
      }
      return uniqueArray;
    },

    handleGetPubsFromIds(mentionIds: Set<string>) {
      const pubs = this.getPubsfromIdsForMediaLists(mentionIds);
      return pubs;
    },

    async handleCreateNewMediaList(name: string): Promise<number> {
      const resp = await this.createMediaList(String(name));
      const newItem = resp.data.value.data;
      this.mediaLists.push(newItem);
      return Number(newItem.id);
    },

    async addDomainsToMediaList(
      publications: {
        url: string;
        media_type: string;
        name: string;
      }[],
      selectedTML: number | null,
    ) {
      if (publications.length === 0) {
        return [];
      }

      let results: {
        success: boolean;
        publication: string;
        message: string;
      }[] = [];

      try {
        const reqQuery = publications.map((publication) => {
          return {
            type: "media-list-domains",
            attributes: {
              name: publication?.name || "",
              media_type: publication.media_type,
              url:
                publication.media_type === "web"
                  ? publication.url
                  : publication.name,
            },
            relationships: {
              media_list: {
                data: {
                  type: "media-lists",
                  id: selectedTML,
                },
              },
            },
          };
        });

        const options: RequestOptions = {
          method: "POST",
          body: { data: reqQuery },
        };

        const resp = await useApiCall(
          `/media_list_domains/bulk_create`,
          options,
        );

        if (resp?.error?.value?.data?.errors) {
          // Note: When bulk_create API returns an error, all publications in the batch
          // will receive the same error message from the first error in the response.
          // This is a limitation of the bulk API endpoint.
          throw resp.error.value.data.errors[0].detail;
        }

        // Invalidate cache for media lists to ensure fresh data
        invalidateApiCache("/media_list_domains");
        invalidateApiCache("/media_lists");

        this.mediaListDomains = this.mediaListDomains.concat(
          resp.data._rawValue.data,
        );

        results = publications.map((publication) => ({
          success: true,
          publication: publication.name,
          message: "dialog.addToTargetMediaList.notifications.addedToTML",
        }));
      } catch (error: any) {
        const errorMessage =
          error === "Url has already been taken"
            ? "dialog.addToTargetMediaList.notifications.duplicatePublication"
            : "dialog.addToTargetMediaList.notifications.failedAddingToTML";

        results = publications.map((publication) => ({
          success: false,
          publication: publication.name,
          message: errorMessage,
        }));
      } finally {
        // Always refresh after adding all domains
        await this.fetchMediaLists();
      }

      return results;
    },
  },
});
